import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

// Auth-store SPA.
//
// После #916 session-JWT в JS НЕ хранится: токен живёт в HttpOnly
// cookie `baton_session`, недоступной скрипту (XSS не вытащит). В JS
// остаётся только НЕсекретный identity-снапшот (`userId`/`orgId`) —
// он же индикатор «залогинен ли»: `identity !== null` означает «у нас
// есть активная серверная сессия» (cookie). Настоящая проверка валидна
// только на сервере по cookie; identity в localStorage — лишь UI-флаг,
// переживающий reload вкладки. На 401 он чистится (`logout`).
//
// `orgId` может быть `null` — org-less сессия (ADR-0038, фаза 1):
// login/register больше не резолвят членство сами. Источник истины об
// активной org сессии — сервер (`GET /me.active_org_id`); localStorage
// хранит лишь UI-кэш последнего известного значения. `<RequireActiveOrg>`
// сверяет его с `/me` и правит стор через `setActiveOrg`.
//
// Server-state (`/me`, /tasks, /queue) живёт в TanStack Query — этот
// store отвечает только за «кто я» как UI-флаг.

// Ключ persist'а в localStorage. Менять синхронно, если когда-то
// поменяется формат identity.
export const AUTH_STORAGE_KEY = "baton.auth.identity";

export interface AuthIdentity {
  /** UUIDv7 user'а — из ответа login/register. */
  readonly userId: string;
  /**
   * UUIDv7 активной организации, `null` — org-less сессия (ADR-0038,
   * фаза 1): активная организация ещё не выбрана.
   */
  readonly orgId: string | null;
}

export interface AuthState {
  /**
   * Identity-снапшот; null = не залогинен. Не секрет (id'шники, не
   * токен) — токен в HttpOnly cookie, в JS его нет.
   */
  readonly identity: AuthIdentity | null;
  /** Сохранить identity (login/register success). */
  login(identity: AuthIdentity): void;
  /** Очистить identity (logout/401). */
  logout(): void;
  /**
   * Проставить активную организацию (после `switchOrganization` или
   * сверки с `/me.active_org_id` в `<RequireActiveOrg>`). No-op, если
   * identity уже нет (гонка с logout'ом) — ставить org разлогиненному
   * снапшоту некуда.
   */
  setActiveOrg(orgId: string | null): void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      identity: null,
      login: (identity) => {
        set({ identity });
      },
      logout: () => {
        // Полная очистка identity-флага: после logout TanStack Query
        // queries с `enabled: identity !== null` сами остановятся,
        // авторизованные запросы перестанут идти. Серверную cookie
        // чистит backend (`POST /auth/logout`).
        set({ identity: null });
      },
      setActiveOrg: (orgId) => {
        set((state) =>
          state.identity === null
            ? state
            : { identity: { ...state.identity, orgId } },
        );
      },
    }),
    {
      name: AUTH_STORAGE_KEY,
      // JSON-storage поверх window.localStorage (default), но
      // явно подсвечиваем выбор.
      storage: createJSONStorage(() => window.localStorage),
      // Сохраняем ТОЛЬКО identity (не секрет). Сами actions / fn
      // никогда не должны попасть в localStorage (а они и не
      // serializable). `partialize` — рекомендованный паттерн
      // zustand persist.
      partialize: (state) => ({
        identity: state.identity,
      }),
    },
  ),
);
